// src/api/fetchers.ts

import type {
  // Shared types
  PublicProfileResponse,
  PatternPublicResponse,
  PatternDisplayResponse,
  ScreenNameCheckResponse,
  PaginatedReviewsResponse,
  ReviewResponse,
  PaginatedFollowersResponse,
  PaginatedFollowingResponse,
  PaginatedFavoritesResponse,
  UserDisplayResponse,
  Paginated,
  // Craft Room APIs
  CraftProjectResponse,
  PatternSearchResult
} from '@mrb91002/shared-types';


import { API_BASE } from './config';

// ----------- Patterns ----------- //

// Used in: Home page
// Get full public patterns (with author reputation merged in)
export async function fetchNewestPatternsFull(
  limit = 5,
  lastKey: Record<string, unknown> | string | null = null
): Promise<Paginated<PatternPublicResponse>> {
  let url = `${API_BASE}/patterns/public/full?limit=${limit}`;
  if (lastKey) {
    const lastKeyStr = typeof lastKey === "string" ? lastKey : JSON.stringify(lastKey);
    url += `&lastKey=${encodeURIComponent(lastKeyStr)}`;
  }
  const resp = await fetch(url);
  if (!resp.ok) throw new Error('Failed to fetch newest patterns (full)');
  return resp.json();
}

// Used in: Home page
// Search patterns with sorting and date filtering via OpenSearch
export async function searchPatternsWithFilters(
  sortBy: 'newest' | 'highest-rated' | 'most-reviewed',
  dateRange: 'day' | 'week' | 'month' | 'year' | 'all',
  limit = 50,
  from = 0
): Promise<{ items: PatternPublicResponse[]; total: number; from: number }> {
  const url = `${API_BASE}/patterns/search-filtered?sortBy=${sortBy}&dateRange=${dateRange}&limit=${limit}&from=${from}`;
  const resp = await fetch(url);
  if (!resp.ok) throw new Error('Failed to search patterns');
  return resp.json();
}





// Used in: Pattern page
// Get full pattern details (latest version)
export async function fetchPatternDetails(
  patternId: string
): Promise<PatternPublicResponse> {
  const resp = await fetch(`${API_BASE}/patterns/${patternId}/details`);
  if (!resp.ok) throw new Error('Failed to fetch pattern details');
  return resp.json();
}

// Used internally by: user-analysis service
// Patterns for a user (screenName)
export async function fetchUserPatterns(
  screenName: string,
  limit = 20,
  lastKey: string | null = null
): Promise<Paginated<PatternDisplayResponse>> {
  let url = `${API_BASE}/user-patterns/${encodeURIComponent(screenName)}?limit=${limit}`;
  if (lastKey) url += `&lastKey=${encodeURIComponent(lastKey)}`;
  const resp = await fetch(url);
  if (!resp.ok) throw new Error('Failed to fetch user patterns');
  return resp.json();
}

// Used in: Profile page
// All patterns by authorId (for profile page patterns tab, paginated)
export async function fetchAuthorPatterns(
  authorId: string,
  lastKey: string | null = null
): Promise<Paginated<PatternDisplayResponse>> {
  let url = `${API_BASE}/author/${encodeURIComponent(authorId)}/patterns`;
  if (lastKey) url += `?lastKey=${encodeURIComponent(lastKey)}`;
  const resp = await fetch(url);
  if (!resp.ok) throw new Error('Failed to fetch author patterns');
  return resp.json();
}

// ----------- Reviews ----------- //

// Used in: Pattern page (PatternReviews component)
// Get all reviews for a pattern (paginated)
export async function fetchAllReviewsForPattern(
  patternId: string,
  limit = 2,
  lastKey: string | null = null
): Promise<PaginatedReviewsResponse> {
  let url = `${API_BASE}/patterns/${encodeURIComponent(patternId)}/reviews?limit=${limit}`;
  if (lastKey) url += `&lastKey=${encodeURIComponent(lastKey)}`;
  const resp = await fetch(url);
  if (!resp.ok) throw new Error('Failed to fetch reviews for pattern');
  return resp.json();
}





// Used internally by: user-analysis service
// Reviews by a user
export async function fetchUserReviews(
  screenName: string,
  limit = 20,
  lastKey: string | null = null
): Promise<Paginated<ReviewResponse>> {
  let url = `${API_BASE}/user-reviews/${encodeURIComponent(screenName)}?limit=${limit}`;
  if (lastKey) url += `&lastKey=${encodeURIComponent(lastKey)}`;
  const resp = await fetch(url);
  if (!resp.ok) throw new Error('Failed to fetch user reviews');
  return resp.json();
}



// ----------- Follows / Followers ----------- //



// ----------- Following/Unfollowing (Secure) ----------- //

// Used in: (Currently unused - moved to secureRequests)
// Follow an artist (by target identity ID)
export async function followArtist(targetUserId: string, idToken: string): Promise<void> {
  const resp = await fetch(
    `${API_BASE}/secure/user/${encodeURIComponent(targetUserId)}/follow`,
    {
      method: "POST",
      headers: {
        Authorization: idToken,
        "Content-Type": "application/json",
      },
    }
  );
  if (!resp.ok) throw new Error("Failed to follow artist");
}

// Used in: (Currently unused - moved to secureRequests)
// Unfollow an artist (by target identity ID)
export async function unfollowArtist(targetUserId: string, idToken: string): Promise<void> {
  const resp = await fetch(
    `${API_BASE}/secure/user/${encodeURIComponent(targetUserId)}/follow`,
    {
      method: "DELETE",
      headers: {
        Authorization: idToken,
        "Content-Type": "application/json",
      },
    }
  );
  if (!resp.ok) throw new Error("Failed to unfollow artist");
}

// ----------- Profile Pages ----------- //

// Used in: Profile page
// Unified public profile page fetch (main profile page, tabs, and all display)
export async function fetchUserProfilePage(
  screenName: string
): Promise<{
  profile: PublicProfileResponse;
  display: UserDisplayResponse;
  patterns: PatternDisplayResponse[];
  followers: UserDisplayResponse[];
  following: UserDisplayResponse[];
}> {
  const resp = await fetch(`${API_BASE}/profile-page/${encodeURIComponent(screenName)}`);
  if (!resp.ok) throw new Error('Failed to fetch user profile page');
  return resp.json();
}

// Used in: AuthContext
// Used in: CallbackHandler component
// Get user profile by identityId (private, self)
export async function fetchUserProfileById(
  identityId: string
): Promise<PublicProfileResponse> {
  const resp = await fetch(`${API_BASE}/user-profiles/by-id/${encodeURIComponent(identityId)}`);
  if (!resp.ok) throw new Error('Failed to fetch user profile by ID');
  return resp.json();
}

// Used in: EditProfile page
// Get user profile by screen name (public)
export async function fetchUserProfileByScreenName(
  screenName: string
): Promise<PublicProfileResponse> {
  const resp = await fetch(`${API_BASE}/user-profiles/by-screen-name/${encodeURIComponent(screenName)}`);
  if (!resp.ok) throw new Error('Failed to fetch user profile by screen name');
  return resp.json();
}



// ----------- Bulk Lookups ----------- //





// ----------- Username/Screen Name Availability ----------- //

// Used in: CreateProfile page
export async function checkUsernameAvailable(
  screenName: string
): Promise<ScreenNameCheckResponse> {
  const resp = await fetch(
    `${API_BASE}/public/check-username?screen_name=${encodeURIComponent(screenName)}`
  );
  if (!resp.ok) throw new Error('Failed to check screen name availability');
  return resp.json();
}

// ----------- FAVORITES (NEW) ----------- //



// ----------- FAVORITES (Paginated) ----------- //
// Used in: (Currently unused)
// Fetch paginated favorited pattern displays for a given user's public profile
export async function fetchUserFavoritesPaginated(
  screenName: string,
  lastKey: string | null = null
): Promise<PaginatedFavoritesResponse> {
  let url = `${API_BASE}/user-favorites/${encodeURIComponent(screenName)}`;
  if (lastKey) url += `?lastKey=${encodeURIComponent(lastKey)}`;
  const resp = await fetch(url);
  if (!resp.ok) throw new Error('Failed to fetch user favorite patterns (paginated)');
  return resp.json();
}

// Fetch paginated followers for a user profile (returns always items array)
export async function fetchUserFollowersPaginated(
  screenName: string,
  lastKey: string | null = null
): Promise<PaginatedFollowersResponse> {
  let url = `${API_BASE}/user-followers/${encodeURIComponent(screenName)}`;
  if (lastKey) url += `?lastKey=${encodeURIComponent(lastKey)}`;
  const resp = await fetch(url);
  if (!resp.ok) throw new Error('Failed to fetch user followers (paginated)');
  return resp.json();
}

// Fetch paginated following for a user profile (returns always items array)
export async function fetchUserFollowingPaginated(
  screenName: string,
  lastKey: string | null = null
): Promise<PaginatedFollowingResponse> {
  let url = `${API_BASE}/user-following/${encodeURIComponent(screenName)}`;
  if (lastKey) url += `?lastKey=${encodeURIComponent(lastKey)}`;
  const resp = await fetch(url);
  if (!resp.ok) throw new Error('Failed to fetch user following (paginated)');
  return resp.json();
}

// ---- CRAFT ROOM PUBLIC ----

/**
 * Get user's craft room (public)
 */
/**
 * Get user's craft room page (public) - includes craft room + materials + projects
 */
export async function fetchCraftRoomPage(screenName: string): Promise<{
  craftRoom: {
    materials: any[];
    [key: string]: any;
  };
  projects: any[];
  materials_lastKey?: string;
  projects_lastKey?: string;
}> {
  const resp = await fetch(`${API_BASE}/craft-rooms/${encodeURIComponent(screenName)}`);
  if (!resp.ok) throw new Error('Failed to fetch craft room page');
  return resp.json();
}

/**
 * Get paginated craft room materials (for "Load More")
 */
export async function fetchCraftRoomMaterials(
  screenName: string,
  lastKey: string | null = null
): Promise<{ items: any[]; lastKey?: string }> {
  let url = `${API_BASE}/craft-rooms/${encodeURIComponent(screenName)}/materials`;
  if (lastKey) url += `?lastKey=${encodeURIComponent(lastKey)}`;
  const resp = await fetch(url);
  if (!resp.ok) throw new Error('Failed to fetch craft room materials');
  return resp.json();
}

// Legacy single craft room API (for backward compatibility)
export async function fetchCraftRoom(screenName: string): Promise<any> {
  const resp = await fetch(`${API_BASE}/craft-rooms/${encodeURIComponent(screenName)}`);
  if (!resp.ok) throw new Error('Failed to fetch craft room');
  const data = await resp.json();
  return data.craftRoom || data; // Handle both new and old response formats
}

/**
 * Get craft project details (public)
 */
export async function fetchCraftProject(projectId: string): Promise<CraftProjectResponse> {
  const resp = await fetch(`${API_BASE}/craft-projects/${encodeURIComponent(projectId)}`);
  if (!resp.ok) throw new Error('Failed to fetch craft project');
  return resp.json();
}

/**
 * Search patterns for craft project creation
 */
export async function searchPatterns(query: string, limit: number = 10, page: number = 1): Promise<PatternSearchResult[]> {
  const resp = await fetch(`${API_BASE}/patterns/search?q=${encodeURIComponent(query)}&limit=${limit}&page=${page}`);
  if (!resp.ok) throw new Error('Failed to search patterns');
  const data = await resp.json();
  return data.results || [];
}

/**
 * Get craft projects that use a specific pattern
 */
export async function fetchPatternCraftProjects(patternId: string, limit: number = 6): Promise<CraftProjectResponse[]> {
  const resp = await fetch(`${API_BASE}/patterns/${encodeURIComponent(patternId)}/craft-projects?limit=${limit}`);
  if (!resp.ok) throw new Error('Failed to fetch pattern craft projects');
  const data = await resp.json();
  return data.items || [];
}

/**
 * Get patterns that use a specific material (via GSI1)
 */
export async function fetchPatternsUsingMaterial(materialId: string, limit: number = 6): Promise<PatternSearchResult[]> {
  const resp = await fetch(`${API_BASE}/materials/${encodeURIComponent(materialId)}/patterns?limit=${limit}`);
  if (!resp.ok) throw new Error('Failed to fetch patterns using material');
  const data = await resp.json();
  return data.patterns || [];
}

/**
 * Get user's craft projects (authenticated)
 */
export async function fetchUserCraftProjects(idToken: string): Promise<{ projects: CraftProjectResponse[] }> {
  const resp = await fetch(`${API_BASE.replace('/api', '/api/secure')}/projects`, {
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  });
  if (!resp.ok) throw new Error('Failed to fetch user craft projects');
  return resp.json();
}

/**
 * Get public craft projects for discovery
 */
export async function fetchPublicCraftProjects(limit: number = 20): Promise<CraftProjectResponse[]> {
  const resp = await fetch(`${API_BASE}/craft-projects/public?limit=${limit}`);
  if (!resp.ok) throw new Error('Failed to fetch public craft projects');
  const data = await resp.json();
  return data.projects || [];
}

/**
 * Get comments for a craft project
 */
export async function fetchProjectComments(projectId: string): Promise<any[]> {
  const resp = await fetch(`${API_BASE}/craft-projects/${encodeURIComponent(projectId)}/comments`);
  if (!resp.ok) throw new Error('Failed to fetch project comments');
  const data = await resp.json();
  return data.comments || [];
}